import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ApiError } from '@/api/client'
import { examplePatternMatch, shadowingRule } from '@/lib/gitRouting'
import { GIT_PROVIDER_OPTIONS, type GitRoutingRule, type UpsertGitRoutingRuleRequest } from '@/types/gitRouting'
import {
  credentialNameOf,
  routingRuleSchema,
  type RoutingRuleFormValues,
} from './RoutingRuleDialog.schema'

/** What an "Add rule" can start from — a test result's owner, for instance. */
export interface RoutingRuleDraft {
  urlPattern?: string
  provider?: string
  credentialName?: string
}

export interface RoutingRuleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The rule being edited; absent for a new rule. */
  rule?: GitRoutingRule
  /** Starting values for a new rule. */
  draft?: RoutingRuleDraft
  rules: GitRoutingRule[]
  /** Every credential name the tenant has or refers to, offered as "Existing". */
  credentialNames: string[]
  /** Saves the rule and puts it at `position`. A rejected ApiError is shown under the fields it names. */
  onSubmit: (request: UpsertGitRoutingRuleRequest, position: number) => Promise<void>
}

/** Add or edit a routing rule (GR2). */
export function RoutingRuleDialog({
  open,
  onOpenChange,
  rule,
  draft,
  rules,
  credentialNames,
  onSubmit,
}: RoutingRuleDialogProps) {
  const isEdit = rule !== undefined
  const positions = isEdit ? rules.length : rules.length + 1
  const schema = useMemo(() => routingRuleSchema(rules, rule?.id), [rules, rule?.id])

  const form = useForm<RoutingRuleFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues(rule, draft, positions, credentialNames),
  })

  // A fresh form every time the dialog opens: what was typed into a cancelled dialog does not come back.
  useEffect(() => {
    if (open) {
      form.reset(initialValues(rule, draft, positions, credentialNames))
    }
    // Only on open: the inputs below change while it is open, and resetting then would discard typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const [urlPattern, provider, credentialMode, position] = useWatch({
    control: form.control,
    name: ['urlPattern', 'provider', 'credentialMode', 'position'],
  })
  const providerNames = credentialNames.filter((n) => n.startsWith(`${provider}:`))
  const shadow = /^https:\/\//i.test(urlPattern ?? '')
    ? shadowingRule(urlPattern, position, rules, rule?.id)
    : undefined

  const submit = form.handleSubmit(async (values) => {
    const request: UpsertGitRoutingRuleRequest = {
      ...(rule ? { id: rule.id } : {}),
      urlPattern: values.urlPattern.trim(),
      provider: values.provider,
      credentialName: credentialNameOf(values),
    }

    try {
      await onSubmit(request, values.position)
      onOpenChange(false)
    } catch (error) {
      if (!(error instanceof ApiError)) {
        throw error
      }

      // The server's refusals, under the field each names; anything unplaced goes to the form as a whole.
      const credentialField = values.credentialMode === 'existing' ? 'existingCredential' : 'newCredentialName'
      const placed = [
        ['urlPattern', 'urlPattern'],
        ['provider', 'provider'],
        ['credentialName', credentialField],
      ] as const
      let any = false
      for (const [serverField, formField] of placed) {
        const message = error.fieldError(serverField)
        if (message) {
          form.setError(formField, { message })
          any = true
        }
      }
      if (!any) {
        form.setError('root', { message: error.message })
      }
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit rule ${rule.position}` : 'Add routing rule'}</DialogTitle>
          <DialogDescription>
            Repositories whose URL matches the pattern use this credential. First matching rule wins.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <FormField
              control={form.control}
              name="urlPattern"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Repository URL pattern</FormLabel>
                  <FormControl>
                    <Input placeholder="https://github.com/owner/*" autoComplete="off" spellCheck={false} {...field} />
                  </FormControl>
                  <FormDescription>
                    * matches anything, including "/". Case-insensitive.
                    {/^https:\/\/\S+/i.test(urlPattern ?? '') && (
                      <span className="block font-mono">Matches: {examplePatternMatch(urlPattern)}</span>
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="provider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provider</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value)
                      // The prefix is the provider's, so an existing name of another provider no longer fits.
                      form.setValue('existingCredential', '')
                    }}
                  >
                    <FormControl>
                      <SelectTrigger aria-label="Provider">
                        <SelectValue placeholder="Choose a provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {GIT_PROVIDER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="credentialMode"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>Credential</FormLabel>
                  <FormControl>
                    <RadioGroup value={field.value} onValueChange={field.onChange} className="space-y-2">
                      <div className="flex items-start gap-3">
                        <RadioGroupItem value="existing" id="credential-existing" className="mt-2.5" />
                        <div className="flex-1 space-y-1">
                          <Label htmlFor="credential-existing" className="font-normal">Existing</Label>
                          <FormField
                            control={form.control}
                            name="existingCredential"
                            render={({ field: existing }) => (
                              <FormItem>
                                <Select
                                  value={existing.value}
                                  onValueChange={existing.onChange}
                                  disabled={credentialMode !== 'existing' || providerNames.length === 0}
                                >
                                  <FormControl>
                                    <SelectTrigger aria-label="Existing credential">
                                      <SelectValue
                                        placeholder={providerNames.length === 0 ? `No ${provider} credentials yet` : 'Choose a credential'}
                                      />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {providerNames.map((name) => (
                                      <SelectItem key={name} value={name}>
                                        {name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <RadioGroupItem value="new" id="credential-new" className="mt-2.5" />
                        <div className="flex-1 space-y-1">
                          <Label htmlFor="credential-new" className="font-normal">New name</Label>
                          <FormField
                            control={form.control}
                            name="newCredentialName"
                            render={({ field: name }) => (
                              <FormItem>
                                <div className="flex items-center gap-1">
                                  <span className="font-mono text-sm text-muted-foreground">{provider}:</span>
                                  <FormControl>
                                    <Input
                                      aria-label="New credential name"
                                      placeholder={provider === 'ado' ? 'org-contoso' : 'owner'}
                                      autoComplete="off"
                                      spellCheck={false}
                                      disabled={credentialMode !== 'new'}
                                      {...name}
                                    />
                                  </FormControl>
                                </div>
                                <FormDescription>You can set its token after saving.</FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="position"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Position</FormLabel>
                  <div className="flex items-center gap-2">
                    <Select value={String(field.value)} onValueChange={(v) => field.onChange(Number(v))}>
                      <FormControl>
                        <SelectTrigger aria-label="Position" className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Array.from({ length: positions }, (_, i) => i + 1).map((p) => (
                          <SelectItem key={p} value={String(p)}>
                            {p}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">of {positions} (first match wins)</span>
                  </div>
                </FormItem>
              )}
            />

            {shadow && (
              <div role="alert" className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <div className="flex-1 space-y-2">
                  <p>
                    This pattern is shadowed by rule {shadow.position} ({shadow.urlPattern}). It will never match.
                    Move it above rule {shadow.position}?
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => form.setValue('position', shadow.position)}
                  >
                    Move to {shadow.position}
                  </Button>
                </div>
              </div>
            )}

            {form.formState.errors.root?.message && (
              <p role="alert" className="text-sm font-medium text-destructive">
                {form.formState.errors.root.message}
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving…' : 'Save rule'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function initialValues(
  rule: GitRoutingRule | undefined,
  draft: RoutingRuleDraft | undefined,
  positions: number,
  credentialNames: string[],
): RoutingRuleFormValues {
  const urlPattern = rule?.urlPattern ?? draft?.urlPattern ?? ''
  const provider = rule?.provider ?? draft?.provider ?? 'github'
  const credentialName = rule?.credentialName ?? draft?.credentialName ?? ''
  const known = credentialName !== '' && credentialNames.some((n) => n.toLowerCase() === credentialName.toLowerCase())
  const suffix = credentialName.startsWith(`${provider}:`) ? credentialName.slice(provider.length + 1) : ''

  return {
    urlPattern,
    provider,
    credentialMode: known ? 'existing' : 'new',
    existingCredential: known ? credentialName : '',
    newCredentialName: known ? '' : suffix,
    position: rule?.position ?? positions,
  }
}
